import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { useFacilityStore } from "../../../../app/z-store/store";
import { 
  getRfqDetailsUrl, 
  addRFQResponseRequestsUrl,
  get_delivery_time_marks,
  get_payment_term_marks
} from "../../../../utils/urls";
import { getItem } from "../../../../utils/localStorage";

const AddRfqResponse = () => {
  const navigate = useNavigate();
  const { rfqId } = useParams();
  const facilityId = useFacilityStore((state) => state.facilityId);

  const [currentUser, setCurrentUser] = useState(null);
  const [rfqDetails, setRfqDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- form state ---
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([{ fileName: "", file: null }]);
  const [vatPercentage, setVatPercentage] = useState(0); // New VAT field
  
  // --- grading system state ---
  const [deliveryTimeMarks, setDeliveryTimeMarks] = useState([]);
  const [paymentTermMarks, setPaymentTermMarks] = useState([]);
  const [selectedDeliveryMark, setSelectedDeliveryMark] = useState(null);
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState(null);
  const [gradingDataLoading, setGradingDataLoading] = useState(false);

  // load logged-in user
  useEffect(() => {
    (async () => {
      try {
        const stored = await getItem("APPUSER");
        const parsed = typeof stored === "string" ? JSON.parse(stored) : stored;
        setCurrentUser(parsed.user || null);
      } catch (err) {
        console.error("Failed to load user:", err);
      }
    })();
  }, []);

  // fetch grading data when facilityId is available
  useEffect(() => {
    if (facilityId) {
      fetchGradingData();
    }
  }, [facilityId]);

  const fetchGradingData = async () => {
    setGradingDataLoading(true);
    try {
      // Fetch delivery time marks
      const deliveryResponse = await makeRequest2(
        `${get_delivery_time_marks}/${facilityId}`,
        'GET'
      );
      
      if (deliveryResponse.success) {
        const deliveryMarks = deliveryResponse.data.data?.deliveryTimeMarks || [];
        setDeliveryTimeMarks(deliveryMarks);
      }

      // Fetch payment term marks
      const paymentResponse = await makeRequest2(
        `${get_payment_term_marks}/${facilityId}`,
        'GET'
      );
      
      if (paymentResponse.success) {
        const paymentMarks = paymentResponse.data.data?.paymentTermMarks || [];
        setPaymentTermMarks(paymentMarks);
      }
    } catch (error) {
      console.error('Error fetching grading data:', error);
      toastify('Error loading grading options', 'error');
    } finally {
      setGradingDataLoading(false);
    }
  };

  // fetch RFQ details & seed our items array
  useEffect(() => {
    if (!rfqId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await makeRequest2(`${getRfqDetailsUrl}/${rfqId}`, "GET");
        if (!res.success) throw new Error(res.error || "Failed to fetch RFQ");
        
        const rfqData = res.data?.data;
        setRfqDetails(rfqData);

        if (rfqData && Array.isArray(rfqData.items)) {
          setItems(
            rfqData.items.map((i) => ({
              itemId: i._id,
              itemName: i.itemName,
              description: i.description,
              unitOfMeasure: i.unitOfMeasure,
              specifications: i.specifications,
              quantity: i.quantity || 0,
              unitPrice: 0,
              totalPrice: 0,
              notes: ""
            }))
          );
        } else {
          setItems([]);
        }
      } catch (err) {
        console.error("Error fetching RFQ:", err);
        toastify(err.message, "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [rfqId]);

  // handlers for price, qty, notes, attachments...
  const handleUnitPriceChange = (idx, value) => {
    const copy = [...items];
    copy[idx].unitPrice = value || 0;
    copy[idx].totalPrice = copy[idx].quantity * copy[idx].unitPrice;
    setItems(copy);
  };
  
  const handleQuantityChange = (idx, value) => {
    const copy = [...items];
    copy[idx].quantity = value || 0;
    copy[idx].totalPrice = copy[idx].quantity * copy[idx].unitPrice;
    setItems(copy);
  };
  
  const handleItemNotesChange = (idx, val) => {
    const copy = [...items];
    copy[idx].notes = val;
    setItems(copy);
  };
  
  const handleFileNameChange = (idx, val) => {
    const copy = [...attachments];
    copy[idx].fileName = val;
    setAttachments(copy);
  };
  
  const handleFileChange = (idx, file) => {
    const copy = [...attachments];
    copy[idx].file = file;
    setAttachments(copy);
  };
  
  const addAttachmentField = () =>
    setAttachments([...attachments, { fileName: "", file: null }]);
  
  const removeAttachmentField = (idx) => {
    const copy = [...attachments];
    copy.splice(idx, 1);
    setAttachments(copy);
  };
  
  const calculateSubTotal = () =>
    items.reduce((sum, it) => sum + (it.totalPrice || 0), 0);

  const calculateVatAmount = () => {
    const subTotal = calculateSubTotal();
    return (subTotal * (vatPercentage || 0)) / 100;
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const vatAmount = calculateVatAmount();
    return subTotal + vatAmount;
  };

  // clear form back to initial RFQ state
  const handleClear = () => {
    if (!rfqDetails) return;
    
    const rfqItems = Array.isArray(rfqDetails.items) ? rfqDetails.items : [];
    setItems(
      rfqItems.map((i) => ({
        itemId: i._id,
        itemName: i.itemName,
        description: i.description,
        unitOfMeasure: i.unitOfMeasure,
        specifications: i.specifications,
        quantity: i.quantity || 0,
        unitPrice: 0,
        totalPrice: 0,
        notes: ""
      }))
    );
    setNotes("");
    setAttachments([{ fileName: "", file: null }]);
    setSelectedDeliveryMark(null);
    setSelectedPaymentTerm(null);
    setVatPercentage(0);
  };

  // submit handler
  const handleSubmit = async () => {
    if (!currentUser?.userId) {
      toastify("User not authenticated", "error");
      return;
    }
    
    // validate unit prices
    for (const it of items) {
      if (it.unitPrice <= 0) {
        toastify(`Please set a valid price for ${it.itemName}`, "error");
        return;
      }
    }
    
    // validate delivery time selection
    if (!selectedDeliveryMark) {
      toastify("Please select a delivery time", "error");
      return;
    }

    // validate payment term selection
    if (!selectedPaymentTerm) {
      toastify("Please select a payment term", "error");
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Format response data according to schema
      const responseItems = items.map(item => ({
        itemId: item.itemId,
        itemName: item.itemName,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
        totalPrice: item.totalPrice,
        notes: item.notes
      }));

      const formData = new FormData();
      formData.append("facilityId", facilityId);
      formData.append("rfqId", rfqId);
      formData.append("supplierId", currentUser.userId);
      formData.append("items", JSON.stringify(responseItems));
      formData.append("notes", notes);
      formData.append("status", "submitted");
      formData.append("vatPercentage", vatPercentage.toString());
      
      // Add grading system data
      formData.append("deliveryDays", selectedDeliveryMark.deliveryDays.toString());
      formData.append("deliveryMarks", selectedDeliveryMark.marks.toString());
      formData.append("paymentTerm", selectedPaymentTerm.paymentTerm);
      formData.append("paymentTermMarks", selectedPaymentTerm.marks.toString());
      
      // Add delivery timeline for compatibility
      formData.append("deliveryTimeline", JSON.stringify({
        value: selectedDeliveryMark.deliveryDays,
        unit: "days",
        daysEquivalent: selectedDeliveryMark.deliveryDays
      }));

      // Add attachments
      attachments.forEach((att, index) => {
        if (att.file && att.fileName) {
          formData.append("attachments", att.file);
          formData.append("attachmentNames", att.fileName);
        }
      });

      const res = await makeRequest2(
        `${addRFQResponseRequestsUrl}/${facilityId}`,
        "POST",
        formData,
        { "Content-Type": "multipart/form-data" }
      );
      
      if (!res.success) throw new Error(res.error || "Submission failed");

      toastify(res.message || "Quotation submitted successfully", "success");
      navigate("/facility/supplier_rfq_management/rfqs_management");
    } catch (err) {
      toastify(err.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // loading / not found
  if (loading) {
    return (
      <Layout>
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="mt-2">Loading RFQ details...</p>
        </div>
      </Layout>
    );
  }
  
  if (!rfqDetails) {
    return (
      <Layout>
        <div className="text-center p-5">
          RFQ not found or you don't have permission to view it.
        </div>
      </Layout>
    );
  }

  const isClosed = rfqDetails.closingDate ? new Date() > new Date(rfqDetails.closingDate) : false;
  const currency = rfqDetails.currency || "KES";

  // Format delivery time marks for dropdown (hide points)
  const deliveryTimeOptions = deliveryTimeMarks.map(mark => ({
    label: `${mark.deliveryDays} days`,
    value: mark,
    deliveryDays: mark.deliveryDays,
    marks: mark.marks
  }));

  // Format payment term marks for dropdown (hide points)
  const paymentTermOptions = paymentTermMarks.map(mark => ({
    label: mark.paymentTerm,
    value: mark,
    paymentTerm: mark.paymentTerm,
    marks: mark.marks
  }));

  return (
    <Layout>
      {/* Breadcrumbs */}
      <div className="page-header mb-3">
        <ul className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Dashboard</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/facility/supplier_rfq_management/rfqs_management">RFQ List</Link>
          </li>
          <li className="breadcrumb-item">Submit Quotation</li>
        </ul>
      </div>

      {/* Back button */}
      <div className="card mb-3">
        <div className="card-header">
          <Link to="/facility/supplier_rfq_management/rfqs_management">
            <i className="ti ti-arrow-narrow-left"></i> Back
          </Link>
        </div>
      </div>

      {/* Closed notice */}
      {isClosed && (
        <div className="card mb-3">
          <div className="card-body alert alert-warning">
            <i className="ti ti-alert-circle me-2"></i>
            This RFQ closed on {new Date(rfqDetails.closingDate).toLocaleDateString()}.
          </div>
        </div>
      )}

      {/* RFQ Details */}
      <div className="card mb-3">
        <div className="card-header">
          <h5>RFQ Details</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <dl className="row">
                <dt className="col-sm-4">Number:</dt>
                <dd className="col-sm-8">{rfqDetails.rfqNumber || "N/A"}</dd>
                <dt className="col-sm-4">Name:</dt>
                <dd className="col-sm-8">{rfqDetails.name || "N/A"}</dd>
                <dt className="col-sm-4">Category:</dt>
                <dd className="col-sm-8">{rfqDetails.category || "N/A"}</dd>
              </dl>
            </div>
            <div className="col-md-6">
              <dl className="row">
                <dt className="col-sm-4">Start:</dt>
                <dd className="col-sm-8">
                  {rfqDetails.startDate ? new Date(rfqDetails.startDate).toLocaleDateString() : "N/A"}
                </dd>
                <dt className="col-sm-4">Closing:</dt>
                <dd className="col-sm-8">
                  {rfqDetails.closingDate ? new Date(rfqDetails.closingDate).toLocaleDateString() : "N/A"}
                </dd>
                <dt className="col-sm-4">Currency:</dt>
                <dd className="col-sm-8">{currency}</dd>
              </dl>
            </div>
          </div>
          {rfqDetails.notes && (
            <div className="mt-3">
              <h6>Notes</h6>
              <p>{rfqDetails.notes}</p>
            </div>
          )}
        </div>
      </div>

      {!isClosed && (
        <>
          {/* Delivery Time & Payment Terms */}
          <div className="card mb-3">
            <div className="card-header">
              <h5>Delivery & Payment Terms*</h5>
            </div>
            <div className="card-body">
              {gradingDataLoading ? (
                <div className="text-center p-3">
                  <div className="spinner-border spinner-border-sm" role="status" />
                  <span className="ms-2">Loading options...</span>
                </div>
              ) : (
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Delivery Time*</label>
                    <Dropdown
                      value={selectedDeliveryMark}
                      options={deliveryTimeOptions}
                      onChange={(e) => setSelectedDeliveryMark(e.value)}
                      placeholder="Select delivery time"
                      className="w-100"
                      disabled={isSubmitting || deliveryTimeOptions.length === 0}
                    />
                    {deliveryTimeOptions.length === 0 && (
                      <small className="text-muted">
                        No delivery time options available. Please contact the facility administrator.
                      </small>
                    )}
                    {selectedDeliveryMark && (
                      <div className="mt-2 p-2 bg-light rounded">
                        <small className="text-muted">
                          Selected: {selectedDeliveryMark.deliveryDays} days delivery
                        </small>
                      </div>
                    )}
                  </div>
                  
                  <div className="col-md-6">
                    <label className="form-label">Payment Terms*</label>
                    <Dropdown
                      value={selectedPaymentTerm}
                      options={paymentTermOptions}
                      onChange={(e) => setSelectedPaymentTerm(e.value)}
                      placeholder="Select payment terms"
                      className="w-100"
                      disabled={isSubmitting || paymentTermOptions.length === 0}
                    />
                    {paymentTermOptions.length === 0 && (
                      <small className="text-muted">
                        No payment term options available. Please contact the facility administrator.
                      </small>
                    )}
                    {selectedPaymentTerm && (
                      <div className="mt-2 p-2 bg-light rounded">
                        <small className="text-muted">
                          Selected: {selectedPaymentTerm.paymentTerm}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quotation Items */}
          <div className="card mb-3">
            <div className="card-header">
              <h5>Quotation Items</h5>
            </div>
            <div className="card-body">
              {items.length === 0 ? (
                <div className="alert alert-info">
                  <i className="ti ti-info-circle me-2"></i>
                  No items found in this RFQ.
                </div>
              ) : (
                items.map((item, idx) => (
                  <div key={idx} className="card mb-3">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <h6>{item.itemName}</h6>
                          {item.description && (
                            <p className="text-muted small">{item.description}</p>
                          )}
                          {item.specifications && (
                            <p>
                              <strong>Specs:</strong> {item.specifications}
                            </p>
                          )}
                        </div>
                        <div className="col-md-6">
                          <div className="row g-2">
                            <div className="col-md-4">
                              <label className="form-label">Qty</label>
                              <InputNumber 
                                value={item.quantity} 
                                onValueChange={(e) => handleQuantityChange(idx, e.value)}
                                className="w-100" 
                                disabled={isSubmitting}
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Unit Price*</label>
                              <InputNumber
                                value={item.unitPrice}
                                onValueChange={(e) => handleUnitPriceChange(idx, e.value)}
                                min={0}
                                mode="currency"
                                currency={currency}
                                className="w-100"
                                disabled={isSubmitting}
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label">Total</label>
                              <InputNumber
                                value={item.totalPrice}
                                mode="currency"
                                currency={currency}
                                className="w-100"
                                disabled
                              />
                            </div>
                            <div className="col-md-12 mt-2">
                              <label className="form-label">Notes (opt)</label>
                              <InputTextarea
                                rows={2}
                                className="w-100"
                                value={item.notes}
                                onChange={(e) => handleItemNotesChange(idx, e.target.value)}
                                disabled={isSubmitting}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}

              {/* VAT and Totals */}
              {items.length > 0 && (
                <div className="row">
                  <div className="col-md-6 offset-md-6">
                    <div className="card">
                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label">VAT % (optional)</label>
                            <InputNumber
                              value={vatPercentage}
                              onValueChange={(e) => setVatPercentage(e.value || 0)}
                              min={0}
                              max={100}
                              suffix="%"
                              className="w-100"
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                        
                        <div className="d-flex justify-content-between mb-2">
                          <span>Subtotal:</span>
                          <span>
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency
                            }).format(calculateSubTotal())}
                          </span>
                        </div>
                        
                        {vatPercentage > 0 && (
                          <div className="d-flex justify-content-between mb-2">
                            <span>VAT ({vatPercentage}%):</span>
                            <span>
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency
                              }).format(calculateVatAmount())}
                            </span>
                          </div>
                        )}
                        
                        <hr />
                        <div className="d-flex justify-content-between">
                          <h5 className="mb-0">Grand Total:</h5>
                          <h5 className="mb-0">
                            {new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency
                            }).format(calculateGrandTotal())}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Attachments */}
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between">
              <h5>Supporting Docs</h5>
              <Button
                icon="ti ti-plus"
                className="p-button-sm"
                onClick={addAttachmentField}
                disabled={isSubmitting}
              />
            </div>
            <div className="card-body">
              {attachments.map((att, i) => (
                <div key={i} className="row mb-3">
                  <div className="col-md-5">
                    <InputText
                      placeholder="Doc name"
                      value={att.fileName}
                      onChange={(e) => handleFileNameChange(i, e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-md-6">
                    <input
                      type="file"
                      className="form-control"
                      onChange={(e) => handleFileChange(i, e.target.files[0])}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    {attachments.length > 1 && (
                      <Button
                        icon="ti ti-trash"
                        className="p-button-sm p-button-danger"
                        onClick={() => removeAttachmentField(i)}
                        disabled={isSubmitting}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Notes */}
          <div className="card mb-3">
            <div className="card-header">
              <h5>Additional Notes</h5>
            </div>
            <div className="card-body">
              <InputTextarea
                rows={4}
                className="w-100"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="card mb-3">
            <div className="card-body text-end">
              <Button
                className="btn btn-outline-secondary me-2"
                onClick={handleClear}
                disabled={isSubmitting}
              >
                Clear
              </Button>
              <Button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSubmitting || !selectedDeliveryMark || !selectedPaymentTerm}
              >
                {isSubmitting ? "Submitting..." : "Submit Quotation"}
              </Button>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default AddRfqResponse;